import * as amplitude from '@amplitude/analytics-browser';

import {
  AMPLITUDE_API_KEY,
  AMPLITUDE_TRACKING,
  getEnvironmentValue,
} from '../../envConfig';
import { describeApiRequest } from '../../utils/apiVersionManager';

export const ANALYTICS_EVENTS = {
  PAGE_VIEWED: 'page_viewed',
  API_REQUEST_FAILED: 'api_request_failed',
  RUNTIME_ERROR: 'runtime_error',
  LOGIN_STARTED: 'login_started',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  APPLY_FOR_CREDIT_VIEWED: 'apply_for_credit_viewed',
  APPLY_FOR_CREDIT_SUBMITTED: 'apply_for_credit_submitted',
  APPLY_FOR_CREDIT_SUCCEEDED: 'apply_for_credit_succeeded',
  APPLY_FOR_CREDIT_PARTIAL: 'apply_for_credit_partial_submission',
  APPLY_FOR_CREDIT_FAILED: 'apply_for_credit_failed',
  APPLY_FOR_INCREASE_CTA_CLICKED: 'apply_for_increase_cta_clicked',
  APPLY_FOR_INCREASE_FORM_VIEWED: 'apply_for_increase_form_viewed',
  APPLY_FOR_INCREASE_SUBMITTED: 'apply_for_increase_submitted',
  APPLY_FOR_INCREASE_SUCCEEDED: 'apply_for_increase_succeeded',
  APPLY_FOR_INCREASE_FAILED: 'apply_for_increase_failed',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_REDIRECTED: 'payment_redirected',
  PAYMENT_UPDATE_SUCCEEDED: 'payment_update_succeeded',
  PAYMENT_UPDATE_FAILED: 'payment_update_failed',
};

let analyticsInitialized = false;
let runtimeHandlersRegistered = false;

const isTruthy = (value) => value === true || value === 'true';

const shouldTrack = () => {
  return (
    isTruthy(getEnvironmentValue(AMPLITUDE_TRACKING)) &&
    Boolean(getEnvironmentValue(AMPLITUDE_API_KEY))
  );
};

const compactObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, currentValue]) => {
    if (
      currentValue !== undefined &&
      currentValue !== null &&
      currentValue !== ''
    ) {
      accumulator[key] = currentValue;
    }
    return accumulator;
  }, {});
};

const getPageContext = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return compactObject({
    path: window.location.pathname,
    search: window.location.search,
    hostname: window.location.hostname,
  });
};

const getErrorMessage = (errorLike) => {
  if (!errorLike) {
    return undefined;
  }

  if (typeof errorLike === 'string') {
    return errorLike;
  }

  if (errorLike instanceof Error) {
    return errorLike.message;
  }

  if (typeof errorLike === 'object') {
    return errorLike.message || errorLike.error || errorLike.reason;
  }

  return String(errorLike);
};

const registerRuntimeErrorHandlers = () => {
  if (runtimeHandlersRegistered || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    trackRuntimeError({
      errorType: 'window_error',
      message: getErrorMessage(event.error || event.message),
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackRuntimeError({
      errorType: 'unhandled_rejection',
      message: getErrorMessage(event.reason),
    });
  });

  runtimeHandlersRegistered = true;
};

export function initializeAnalytics() {
  if (!shouldTrack() || analyticsInitialized) {
    return;
  }

  amplitude.init(getEnvironmentValue(AMPLITUDE_API_KEY), {
    defaultTracking: false,
    autocapture: false,
  });
  analyticsInitialized = true;
  registerRuntimeErrorHandlers();
}

export function trackEvent(eventType, eventProperties = {}) {
  if (!shouldTrack()) {
    return;
  }

  initializeAnalytics();
  amplitude.track(eventType, {
    ...getPageContext(),
    ...compactObject(eventProperties),
  });
}

export function trackPageView(eventProperties = {}) {
  trackEvent(ANALYTICS_EVENTS.PAGE_VIEWED, eventProperties);
}

export function setAnalyticsUser(user = {}) {
  if (!shouldTrack()) {
    return;
  }

  initializeAnalytics();

  const userId =
    user.uuid || user.id || user.oktaSub || user.sub || user.email || undefined;
  if (userId) {
    amplitude.setUserId(String(userId));
  }

  const identify = new amplitude.Identify();
  if (user.email) {
    identify.set('email', user.email);
  }
  if (user.userType) {
    identify.set('user_type', user.userType);
  }
  if (user.grower_id) {
    identify.set('grower_id', user.grower_id);
  }
  if (user.oktaSub || user.sub) {
    identify.set('okta_sub', user.oktaSub || user.sub);
  }

  amplitude.identify(identify);
}

export function trackApiRequestError({
  url,
  method,
  status,
  errorMessage,
  responseMessage,
  analyticsContext,
}) {
  const requestDetails = describeApiRequest(url);

  trackEvent(ANALYTICS_EVENTS.API_REQUEST_FAILED, {
    method: method ? method.toUpperCase() : undefined,
    status,
    error_message: errorMessage || responseMessage,
    endpoint_id: requestDetails.endpointId,
    endpoint_group: requestDetails.endpointGroup,
    endpoint_group_title: requestDetails.endpointGroupTitle,
    api_version: requestDetails.effectiveVersion,
    request_path: requestDetails.path,
    ...compactObject(analyticsContext),
  });
}

export function trackRuntimeError({
  errorType,
  message,
  source,
  line,
  column,
}) {
  trackEvent(ANALYTICS_EVENTS.RUNTIME_ERROR, {
    error_type: errorType,
    error_message: message,
    source,
    line,
    column,
  });
}

export function __resetAnalyticsForTests() {
  analyticsInitialized = false;
  runtimeHandlersRegistered = false;
}
